import {
  useState,
  useEffect,
  useRef,
  createRef,
} from 'react';
import PropTypes from 'prop-types';
import UILoader from 'components/UI/Loader';
import { Form } from 'formsy-semantic-ui-react';
import { Label, Button } from 'semantic-ui-react';
import { useTranslation } from 'next-i18next';
import UIPhoneInput from 'components/UI/PhoneInput';
import { isValidNumber } from 'libphonenumber-js';
import SMSCodeFields from 'components/Forms/SMSCode';
import isEmpty from 'lodash/isEmpty';
import styles from './sign-in-form.module.scss';
import { useRouter } from 'next/router';
import PhoneInfoBlock from '../PhoneInfoBlock';
import { SmartCaptcha } from '@yandex/smart-captcha';
import getConfig from 'next/config';

const label = <Label color="red" pointing />;

const SignInForm = ({ signIn, smsCode, authenticateByCode }) => {
  const { t } = useTranslation('common');
  const [phone, setPhone] = useState('');
  const [valid, setValid] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [smsCodeLoading, setSmsCodeLoading] = useState(false);
  const [codeScope, setCodeScope] = useState('sms');
  const [captchaToken, setCaptchaToken] = useState(null);
  const router = useRouter();
  const phoneQueryParam = router.query.phone;
  const instanceTimeoutRef = useRef(null);


  useEffect(() => {
    if (phoneQueryParam) {
      setPhone(phoneQueryParam);
    }
    return () => {
      clearTimeout(instanceTimeoutRef.current);
      setCaptchaToken(null);
    }
  }, [])

  const authenticate = (model) => {
    authenticateByCode(model).then(() => {
      setAuthed(true);
    })
  };

  return (
    <div className={styles.root}>
      <UILoader active={signIn.loading || smsCode.loading} />

      <Form
        onInvalid={() => { setValid(false) }}
        onValid={() => { setValid(true) }}
        onValidSubmit={authenticate}
      >
        <If condition={(isEmpty(phone) || !isValidNumber(phone) || !captchaToken)}>
          <UIPhoneInput
            required
            name="phone"
            autoComplete="off"
            placeholder={t('auth.phone')}
            errorLabel={label}
            validations={{ isValidPhone: true }}
            validationErrors={{
              isDefaultRequiredValue: t('formErrors.required'),
              isValidPhone: t('formErrors.incorrectPhonePattern')
            }}
            value={phone}
            onChange={(event, data) => {
              setPhone(data.value);
            }}
            newDesign
            withIcon
          />

          {valid && (
            <SmartCaptcha
              sitekey={getConfig().publicRuntimeConfig.SMART_CAPTCHA_SITE_KEY}
              onSuccess={setCaptchaToken}
            />
          )}
        </If>


        <If condition={!isEmpty(phone) && isValidNumber(phone) && captchaToken}>
          <PhoneInfoBlock
            phone={phone}
            codeScope={codeScope}
            onClickChangePhone={() => {
              setPhone('');
              clearTimeout(instanceTimeoutRef.current);
              instanceTimeoutRef.current = setTimeout(() => {
                const phoneInput = document.querySelector('.react-tel-input>input');
                phoneInput.focus();
              }, 200);
            }}
          />
          <SMSCodeFields
            autoFocus={true}
            phone={phone}
            captcha={captchaToken}
            onNotExistUserError={() => { setPhone('') }}
            onLoading={(value) => { setSmsCodeLoading(value) }}
            codeScope={codeScope}
            onChangeCodeScope={(value) => {
              setCodeScope(value)
            }}
            scope="AUTHENTICATE_EMPLOYEE"
          />
        </If>

        <div className={styles.actions}>
          <button
            className="z-button button-lg full-width"
            color="teal"
            size="large"
            disabled={!valid || authed || smsCodeLoading}
          >
            {t('auth.submit')}
          </button>
        </div>
      </Form>
    </div>
  )
};

SignInForm.propTypes = {
  smsCode: PropTypes.object.isRequired,
  signIn: PropTypes.object.isRequired,
  authenticateByCode: PropTypes.func.isRequired
};

export default SignInForm;